import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import type { TFunction } from "i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { DocumentDetails } from "./DocumentDetails";
import { DocumentLinker } from "./DocumentLinker";
import { QuoteAdvanceGenerator } from "./QuoteAdvanceGenerator";
import {
  Plus,
  RefreshCw,
  Link2,
  Eye,
  Trash2,
  Sparkles,
  ExternalLink,
  Download,
  Loader2,
  MoreHorizontal,
  FileSignature,
  ClipboardCheck,
  Mail,
  Receipt,
  Building2,
  Wrench,
  AlertTriangle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePickerPopover } from "@/components/ui/date-picker-popover";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  computeTotalsFromSuperfakturaLineItems,
  toMoneyNumber,
} from "@/lib/superfaktura-document-amounts";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { getDateFnsLocale } from "@/lib/date-fns-locale";
import EmailComposer from "@/components/email/EmailComposer";
import { mojaSuperfakturaDocumentViewUrl } from "@/lib/superfaktura-moja-urls";
import {
  isDocumentRelatedToDocument as sharedIsDocumentRelatedToDocument,
  isDocumentRelatedToQuote as sharedIsDocumentRelatedToQuote,
  type RelationDoc,
} from "@shared/document-relations";
import { documentLifecycle, type LifecycleNextStep } from "@shared/document-lifecycle";
import { resolveQuoteIdFromLatestDeposit } from "@shared/latest-deposit-anchor";
import { DealDocumentTimeline, type DealDocumentTimelineDocType } from "./DealDocumentTimeline";
import {
  mapTimelineData,
  listQuotesWithoutDeposit,
  quoteHasDepositInvoice,
  resolveTimelineActiveQuoteId,
} from "./deal-document-timeline-mapper";
import { printPdfBlobs } from "@/lib/print-pdf-blobs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface SuperfakturaDocumentsTabProps {
  dealId: string;
  dealEmail?: string;
  /** ID of the anchor quote document (from deals.anchor_document_id). */
  anchorDocumentId?: string | null;
}

interface LinkedDocument {
  id: string;
  sf_document_id: string;
  document_type: string;
  document_number?: string;
  document_name?: string;
  total_amount?: number;
  subtotal_amount?: number;
  currency?: string;
  document_date?: string;
  due_date?: string;
  status?: string;
  pdf_url?: string;
  raw_data?: any;
  extracted_data?: any;
  document_items?: Array<any>;
  last_synced_at?: string | null;
  customer_ico?: string | null;
  created_at?: string | null;
  tax_rate?: number | null;
  notes?: string | null;
}

/**
 * List cell: bold = Celková suma (s DPH, po zľave); zátvorky = Suma bez DPH.
 * Line-item sums alone match “suma pred zľavou” when a document-level discount exists; prefer `extracted_data.financial` or DB totals when they disagree.
 */
function getDocumentListAmountDisplay(doc: LinkedDocument): {
  primary: number | null;
  secondary: number | null;
  secondaryIsWithVat: boolean;
} {
  const fin = doc.extracted_data?.financial as { total?: unknown; subtotal?: unknown } | undefined;
  if (fin != null) {
    const total = toMoneyNumber(fin.total);
    const sub = toMoneyNumber(fin.subtotal);
    const finHasAmounts = total !== 0 || sub !== 0;
    if (finHasAmounts || !doc.document_items?.length) {
      const secondary = Math.abs(total - sub) > 0.005 ? sub : null;
      return { primary: total, secondary, secondaryIsWithVat: false };
    }
  }

  const computed = computeTotalsFromSuperfakturaLineItems(doc.document_items, {
    notes: doc.notes,
    tax_rate: doc.tax_rate,
    extracted_financial_taxRate: doc.extracted_data?.financial?.taxRate,
  });
  const dbTotal = doc.total_amount != null ? toMoneyNumber(doc.total_amount) : NaN;
  const dbSub = doc.subtotal_amount != null ? toMoneyNumber(doc.subtotal_amount) : NaN;
  if (computed != null && Number.isFinite(dbTotal)) {
    const lineGross = computed.grossTotal;
    if (Math.abs(lineGross - dbTotal) > 0.05) {
      const secondary = Number.isFinite(dbSub) && Math.abs(dbTotal - dbSub) > 0.005 ? dbSub : null;
      return { primary: dbTotal, secondary, secondaryIsWithVat: false };
    }
  }

  if (computed) {
    const { netTotal, grossTotal } = computed;
    const diff = Math.abs(grossTotal - netTotal);
    return {
      primary: netTotal,
      secondary: diff > 0.005 ? grossTotal : null,
      secondaryIsWithVat: diff > 0.005,
    };
  }
  const sub = doc.subtotal_amount;
  const tot = doc.total_amount;
  if (sub != null && tot != null) {
    const subN = Number(sub);
    const totN = Number(tot);
    if (Number.isFinite(subN) && Number.isFinite(totN) && Math.abs(totN - subN) > 0.005) {
      return { primary: subN, secondary: totN, secondaryIsWithVat: totN > subN };
    }
    return { primary: subN, secondary: null, secondaryIsWithVat: false };
  }
  if (tot != null && Number.isFinite(Number(tot))) {
    return { primary: Number(tot), secondary: null, secondaryIsWithVat: false };
  }
  if (sub != null && Number.isFinite(Number(sub))) {
    return { primary: Number(sub), secondary: null, secondaryIsWithVat: false };
  }
  return { primary: null, secondary: null, secondaryIsWithVat: false };
}

function documentHasCompanyIco(doc: LinkedDocument): boolean {
  const ico = (doc as any).customer_ico ?? doc.extracted_data?.customer?.ico;
  if (ico && String(ico).replace(/\D/g, "").length >= 8) return true;
  const raw = doc.raw_data as any;
  if (raw?.client_data?.ico || raw?.ClientData?.ico || raw?.Invoice?.client_data) return true;
  if (typeof raw?.Invoice?.client_data === "string") {
    try {
      const parsed = JSON.parse(raw.Invoice.client_data);
      if (parsed?.Client?.ico || parsed?.ico) return true;
    } catch { /* ignore */ }
  }
  return false;
}

const getDocumentViewUrl = (doc: LinkedDocument): string | null => {
  // Prefer explicit PDF URL
  if (doc.pdf_url) return doc.pdf_url;

  const raw = doc.raw_data as any;
  if (raw) {
    if (raw.Invoice?.pdf_url) return raw.Invoice.pdf_url;
    if (raw.Quote?.pdf_url) return raw.Quote.pdf_url;
    if (raw.pdf_url) return raw.pdf_url;
    if (raw.pdf) return raw.pdf;
  }

  const extracted = doc.extracted_data as any;
  if (extracted) {
    if (extracted.pdfUrl) return extracted.pdfUrl;
    if (extracted.pdf_url) return extracted.pdf_url;
  }

  // If no PDF URL, open Superfaktúra web viewer (quotes and invoices share /invoices/view/:id)
  if (doc.sf_document_id) {
    return mojaSuperfakturaDocumentViewUrl(doc.sf_document_id);
  }

  return null;
};

const documentTypeKeyMap: Record<string, string> = {
  quote: "typeQuote",
  invoice: "typeFinalInvoice",
  deposit_invoice: "typeDepositInvoice",
  tax_document: "typeTaxDocument",
  contract: "typeContract",
  proforma: "typeProforma",
  credit_note: "typeCreditNote",
  expense: "typeExpense",
};

/** Strip color by document type (sáv a sor bal szélén) */
function getDocumentTypeStripColor(documentType?: string): string {
  switch (documentType) {
    case "quote": return "border-l-blue-500";
    case "deposit_invoice": return "border-l-amber-500";
    case "tax_document": return "border-l-teal-500";
    case "invoice": return "border-l-green-600";
    case "contract": return "border-l-violet-500";
    case "proforma": return "border-l-sky-500";
    case "credit_note": return "border-l-rose-400";
    case "expense": return "border-l-slate-500";
    default: return "border-l-slate-300";
  }
}

// Csak használt típusok: Cenová ponuka, Zálohová, Daňový doklad, Konečná faktúra, Zmluvy, Dobropis
const DOC_TYPE_LEGEND_ORDER: Array<keyof typeof documentTypeKeyMap> = [
  "quote", "deposit_invoice", "tax_document", "invoice", "contract", "credit_note",
];
const DOC_TYPE_STRIP_CLASS: Record<string, string> = {
  quote: "bg-blue-500",
  deposit_invoice: "bg-amber-500",
  tax_document: "bg-teal-500",
  invoice: "bg-green-600",
  contract: "bg-violet-500",
  proforma: "bg-sky-500",
  credit_note: "bg-rose-400",
  expense: "bg-slate-500",
};

/** Order of related doc types under a quote: előlegszámla, daňový doklad, faktúra, zmluvy */
const RELATED_DOC_TYPE_ORDER = ["deposit_invoice", "tax_document", "invoice", "contract", "proforma", "credit_note"];

/**
 * Local adapters around the shared resolver. The UI's `LinkedDocument` is a
 * superset of `RelationDoc`, so we just forward the call.
 *
 * Single source of truth lives in `shared/document-relations.ts` — also used
 * by the backend (sync, repair, create-final-invoice) and by
 * `QuoteDocumentHierarchy.tsx`.
 */
function isDocumentRelatedToQuote(
  doc: LinkedDocument,
  quoteId: string,
  quoteDocumentNumber?: string,
  quoteSfDocumentId?: string,
): boolean {
  return sharedIsDocumentRelatedToQuote(
    doc as unknown as RelationDoc,
    quoteId,
    quoteDocumentNumber,
    quoteSfDocumentId,
  );
}

function isDocumentRelatedToDocument(
  doc: LinkedDocument,
  parentId: string,
  parent?: LinkedDocument,
): boolean {
  return sharedIsDocumentRelatedToDocument(
    doc as unknown as RelationDoc,
    parentId,
    parent as unknown as RelationDoc | undefined,
  );
}

type HierarchyRow =
  | { kind: "quote"; document: LinkedDocument }
  | { kind: "child"; document: LinkedDocument; parentQuoteId: string }
  | { kind: "grandchild"; document: LinkedDocument; parentQuoteId: string; parentDocumentId: string; displayAsTaxDocument?: boolean }
  | { kind: "orphan_child"; document: LinkedDocument; parentOrphanId: string; displayAsTaxDocument?: boolean }
  | { kind: "contract"; contract: { id: string; template_id?: string; template_name?: string; pdf_filename?: string; document_ids?: string[] }; parentQuoteId: string }
  | { kind: "contract_under_doc"; contract: { id: string; template_id?: string; template_name?: string; pdf_filename?: string; document_ids?: string[] }; parentQuoteId: string; parentDocumentId: string }
  | { kind: "standalone_contract"; contract: { id: string; template_id?: string; template_name?: string; pdf_filename?: string; document_ids?: string[] } }
  | { kind: "orphan"; document: LinkedDocument };

const HIERARCHY_DEBUG = typeof window !== "undefined" && /[?&]debug=hierarchy/i.test(window.location.search);

function buildHierarchyRows(
  documents: LinkedDocument[],
  contracts: Array<{ id: string; template_id?: string; template_name?: string; pdf_filename?: string; document_ids?: string[] }>
): HierarchyRow[] {
  if (HIERARCHY_DEBUG) {
    console.log("[Hierarchy] Documents for matching:", documents.map((d) => ({
      id: d.id,
      document_type: d.document_type,
      document_number: d.document_number,
      document_name: d.document_name,
      related_document_id: (d as any).related_document_id,
      related_document_number: (d as any).related_document_number,
    })));
  }
  const quotes = documents
    .filter((d) => d.document_type === "quote")
    .sort((a, b) => {
      const byDate = new Date(b.document_date || 0).getTime() - new Date(a.document_date || 0).getTime();
      if (byDate !== 0) return byDate;
      const na = (a.document_number ?? a.document_name ?? "").replace(/\D/g, "") || "0";
      const nb = (b.document_number ?? b.document_name ?? "").replace(/\D/g, "") || "0";
      const byNum = Number(nb) - Number(na);
      if (byNum !== 0) return byNum;
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });
  const usedDocIds = new Set<string>();
  const usedContractIds = new Set<string>();
  const rows: HierarchyRow[] = [];
  const isProtokol = (c: any) => {
    const name = (c.template_name ?? "").toLowerCase();
    return c.template_id === "odovzdavaci-protokol" || name.includes("odovzdávací") || name.includes("preberací");
  };

  for (const quote of quotes) {
    const quoteId = quote.id;
    const quoteDocumentNumber = quote.document_number ?? quote.sf_document_id;
    const quoteSfDocumentId = quote.sf_document_id;
    rows.push({ kind: "quote", document: quote });

    const relatedToQuote = documents.filter(
      (d) =>
        d.id !== quoteId &&
        (d.document_type === "deposit_invoice" ||
          d.document_type === "tax_document" ||
          d.document_type === "invoice" ||
          d.document_type === "contract" ||
          d.document_type === "proforma" ||
          d.document_type === "credit_note") &&
        isDocumentRelatedToQuote(d, quoteId, quoteDocumentNumber, quoteSfDocumentId)
    );
    if (HIERARCHY_DEBUG) {
      console.log(`[Hierarchy] Quote ${quoteDocumentNumber} (${quoteId}): relatedToQuote=`, relatedToQuote.map((d) => ({ id: d.id, document_name: d.document_name, document_number: d.document_number })));
    }
    const directRelated = relatedToQuote.filter(
      (d) => !relatedToQuote.some((parent) => parent.id !== d.id && isDocumentRelatedToDocument(d, parent.id, parent))
    );
    // Grandchildren: docs related to directRelated (e.g. Daňový -> Zálohová), even if not directly related to quote
    const grandchildDocs = documents.filter(
      (d) =>
        d.id !== quoteId &&
        !directRelated.some((dr) => dr.id === d.id) &&
        directRelated.some((parent) => isDocumentRelatedToDocument(d, parent.id, parent))
    );

    // Order: Zálohová → Zmluva (contract) → Daňový doklad (grandchild of Zálohová) → rest
    const depositInvoices = directRelated.filter((d) => d.document_type === "deposit_invoice");
    // Daňový doklad: tax_document OR invoice grandchild of deposit (SF may store Daňový as "invoice")
    const taxDocGrandchildren = grandchildDocs.filter((d) => {
      const parent = directRelated.find((p) => isDocumentRelatedToDocument(d, p.id, p));
      return (
        parent?.document_type === "deposit_invoice" &&
        (d.document_type === "tax_document" || d.document_type === "invoice")
      );
    });
    const otherDirect = directRelated.filter(
      (d) => d.document_type !== "deposit_invoice"
    );
    const otherGrandchildren = grandchildDocs.filter((d) => {
      const parent = directRelated.find((p) => isDocumentRelatedToDocument(d, p.id, p));
      return !(parent?.document_type === "deposit_invoice" && (d.document_type === "tax_document" || d.document_type === "invoice"));
    });

    const sortByTypeAndDate = (a: LinkedDocument, b: LinkedDocument) => {
      const ai = RELATED_DOC_TYPE_ORDER.indexOf(a.document_type);
      const bi = RELATED_DOC_TYPE_ORDER.indexOf(b.document_type);
      if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      return (a.document_date || "").localeCompare(b.document_date || "");
    };

    // 1) Zálohová faktúra(s)
    for (const d of depositInvoices.sort(sortByTypeAndDate)) {
      usedDocIds.add(d.id);
      rows.push({ kind: "child", document: d, parentQuoteId: quoteId });
    }

    // 2) Zmluva o dielo (under quote; Odovzdávací protokol goes under Konečná faktúra)
    const docsInQuoteSubtree = new Set([
      quoteId,
      ...directRelated.map((d) => d.id),
      ...grandchildDocs.map((d) => d.id),
    ]);
    const zmluvaContracts = contracts.filter((c: any) => {
      if (!c.document_ids || !Array.isArray(c.document_ids)) return false;
      const isRelated = c.document_ids.some((docId: string) => docsInQuoteSubtree.has(docId));
      const name = (c.template_name ?? "").toLowerCase();
      const isZmluva = c.template_id === "zmluva-o-dielo" || name.includes("zmluva");
      return isRelated && isZmluva;
    });
    for (const c of zmluvaContracts) {
      usedContractIds.add(c.id);
      rows.push({ kind: "contract", contract: c, parentQuoteId: quoteId });
    }

    const protokolUnderDoc = (docId: string) =>
      contracts.filter((c: any) => !usedContractIds.has(c.id) && isProtokol(c) && c.document_ids?.includes(docId));

    // 3) Daňový doklad + Konečná faktúra (grandchildren of deposit_invoice) + Odovzdávací protokol under Konečná
    for (const deposit of depositInvoices) {
      const taxChildren = taxDocGrandchildren.filter((doc) => isDocumentRelatedToDocument(doc, deposit.id, deposit));
      for (const child of taxChildren.sort(sortByTypeAndDate)) {
        usedDocIds.add(child.id);
        rows.push({
          kind: "grandchild",
          document: child,
          parentQuoteId: quoteId,
          parentDocumentId: deposit.id,
          displayAsTaxDocument: child.document_type === "invoice", // SF may store Daňový as invoice
        });
        for (const pc of protokolUnderDoc(child.id)) {
          usedContractIds.add(pc.id);
          rows.push({ kind: "contract_under_doc", contract: pc, parentQuoteId: quoteId, parentDocumentId: child.id });
        }
      }
    }

    // 4) Rest: invoice, proforma, credit_note + their grandchildren + Odovzdávací protokol under Konečná
    for (const d of otherDirect.sort(sortByTypeAndDate)) {
      usedDocIds.add(d.id);
      rows.push({ kind: "child", document: d, parentQuoteId: quoteId });
      for (const pc of protokolUnderDoc(d.id)) {
        usedContractIds.add(pc.id);
        rows.push({ kind: "contract_under_doc", contract: pc, parentQuoteId: quoteId, parentDocumentId: d.id });
      }
      const children = otherGrandchildren.filter((doc) => isDocumentRelatedToDocument(doc, d.id, d));
      for (const child of children.sort(sortByTypeAndDate)) {
        usedDocIds.add(child.id);
        rows.push({ kind: "grandchild", document: child, parentQuoteId: quoteId, parentDocumentId: d.id });
        for (const pc of protokolUnderDoc(child.id)) {
          usedContractIds.add(pc.id);
          rows.push({ kind: "contract_under_doc", contract: pc, parentQuoteId: quoteId, parentDocumentId: child.id });
        }
      }
    }
  }

  const orphans = documents.filter(
    (d) =>
      !usedDocIds.has(d.id) &&
      (d.document_type !== "quote" || !quotes.some((q) => q.id === d.id)) &&
      !quotes.some((q) =>
        isDocumentRelatedToQuote(d, q.id, q.document_number ?? q.sf_document_id, q.sf_document_id)
      ) &&
      !documents.some((parent) => parent.id !== d.id && isDocumentRelatedToDocument(d, parent.id, parent))
  );
  orphans.sort((a, b) => {
    const byDate = new Date(b.document_date || 0).getTime() - new Date(a.document_date || 0).getTime();
    if (byDate !== 0) return byDate;
    const na = (a.document_number ?? a.document_name ?? "").replace(/\D/g, "") || "0";
    const nb = (b.document_number ?? b.document_name ?? "").replace(/\D/g, "") || "0";
    const byNum = Number(nb) - Number(na);
    if (byNum !== 0) return byNum;
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });
  const sortByTypeAndDate = (a: LinkedDocument, b: LinkedDocument) => {
    const ai = RELATED_DOC_TYPE_ORDER.indexOf(a.document_type);
    const bi = RELATED_DOC_TYPE_ORDER.indexOf(b.document_type);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    return (a.document_date || "").localeCompare(b.document_date || "");
  };
  const protokolUnderOrphanDoc = (docId: string) =>
    contracts.filter((c: any) => !usedContractIds.has(c.id) && isProtokol(c) && c.document_ids?.includes(docId));
  for (const orphan of orphans) {
    usedDocIds.add(orphan.id);
    rows.push({ kind: "orphan", document: orphan });
    for (const pc of protokolUnderOrphanDoc(orphan.id)) {
      usedContractIds.add(pc.id);
      rows.push({ kind: "contract_under_doc", contract: pc, parentQuoteId: "", parentDocumentId: orphan.id });
    }
    const orphanChildren = documents.filter(
      (d) => !usedDocIds.has(d.id) && isDocumentRelatedToDocument(d, orphan.id, orphan)
    );
    for (const child of orphanChildren.sort(sortByTypeAndDate)) {
      usedDocIds.add(child.id);
      const displayAsTaxDocument = child.document_type === "invoice" && (orphan.document_type === "deposit_invoice");
      rows.push({
        kind: "orphan_child",
        document: child,
        parentOrphanId: orphan.id,
        displayAsTaxDocument,
      });
      for (const pc of protokolUnderOrphanDoc(child.id)) {
        usedContractIds.add(pc.id);
        rows.push({ kind: "contract_under_doc", contract: pc, parentQuoteId: "", parentDocumentId: child.id });
      }
    }
  }
  const remainingOrphans = documents.filter((d) => !usedDocIds.has(d.id) && d.document_type !== "quote");
  if (HIERARCHY_DEBUG) {
    console.log("[Hierarchy] Orphans:", remainingOrphans.map((d) => ({ id: d.id, document_name: d.document_name, document_number: d.document_number })));
  }
  for (const d of remainingOrphans.sort(sortByTypeAndDate)) {
    rows.push({ kind: "orphan", document: d });
  }

  // Standalone contracts: not placed under any quote (e.g. document_ids point to orphan docs)
  for (const c of contracts) {
    if (!usedContractIds.has(c.id)) {
      rows.push({ kind: "standalone_contract", contract: c });
    }
  }

  return rows;
}

const ZMLUVA_EMAIL_TEMPLATE_HTML = `
<p>Dobrý deň,</p>
<p>V prílohe Vám zasielame:</p>
<ul>
  <li>Zmluva o dielo</li>
  <li>Zálohová faktúra</li>
  <li>Cenová ponuka</li>
</ul>
<p>Prosím o podpísanie zmluvy a uhradenie zálohovej faktúry.</p>
<p>S pozdravom</p>
`.trim();

function ContractEmailDialog({
  dealId,
  dealEmail,
  contractId,
  documentIds,
  pdfFilename,
  documents,
  onClose,
  onSent,
}: {
  dealId: string;
  dealEmail?: string;
  contractId: string;
  documentIds: string[];
  pdfFilename: string;
  documents: LinkedDocument[];
  onClose: () => void;
  onSent: () => void;
}) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const all: File[] = [];
        const contractRes = await fetch(apiUrl(`/api/crm/deals/${dealId}/contracts/${contractId}`), { credentials: "include" });
        if (!contractRes.ok) throw new Error("Contract PDF");
        const contractBlob = await contractRes.blob();
        all.push(new File([contractBlob], pdfFilename || "Zmluva_o_dielo.pdf", { type: "application/pdf" }));

        const quoteId = documentIds[0];
        const depositId = documentIds[1];
        const getDoc = (id: string) => documents.find((d) => d.id === id);
        const docFilename = (doc: LinkedDocument) => (doc.document_number || doc.document_name || doc.id) + ".pdf";

        if (depositId) {
          const depRes = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents/${depositId}/download`), { credentials: "include" });
          if (depRes.ok) {
            const blob = await depRes.blob();
            const depDoc = getDoc(depositId);
            all.push(new File([blob], docFilename(depDoc || { id: depositId } as LinkedDocument), { type: "application/pdf" }));
          }
        }
        if (quoteId) {
          const quoteRes = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents/${quoteId}/download`), { credentials: "include" });
          if (quoteRes.ok) {
            const blob = await quoteRes.blob();
            const quoteDoc = getDoc(quoteId);
            all.push(new File([blob], docFilename(quoteDoc || { id: quoteId } as LinkedDocument), { type: "application/pdf" }));
          }
        }
        if (!cancelled) setFiles(all);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, contractId, documentIds, pdfFilename, documents]);

  const to = dealEmail ? [{ email: dealEmail }] : [];

  if (files !== null) {
    return (
      <EmailComposer
        dealId={dealId}
        to={to}
        initialSubject={t("documents.zmluvaEmailSubject", "Zmluva o dielo a príložené dokumenty")}
        initialBody={ZMLUVA_EMAIL_TEMPLATE_HTML}
        initialAttachmentFiles={files}
        onClose={onClose}
        onSent={onSent}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("documents.emailWithAttachments", "E-mail – Zmluva + zálohová + cenová ponuka")}</DialogTitle>
        </DialogHeader>
        {error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{t("common.loading", "Načítavam…")}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DealDocumentsActionsMenu({
  t,
  align,
  syncing,
  syncPending,
  onSync,
  repairPending,
  onRepair,
  autoLinking,
  autoLinkPending,
  onAutoLink,
  onOpenManualLink,
}: {
  t: TFunction;
  align: "end" | "center";
  syncing: boolean;
  syncPending: boolean;
  onSync: () => void;
  repairPending: boolean;
  onRepair: () => void;
  autoLinking: boolean;
  autoLinkPending: boolean;
  onAutoLink: () => void;
  onOpenManualLink: () => void;
}) {
  const syncBusy = syncing || syncPending;
  const autoBusy = autoLinking || autoLinkPending;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={t("common.more")}
          title={t("common.more")}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[13rem]">
        <DropdownMenuItem onClick={onSync} disabled={syncBusy}>
          <RefreshCw className={cn("h-4 w-4 mr-2 shrink-0", syncBusy && "animate-spin")} />
          {syncBusy ? t("documents.syncing") : t("documents.refresh")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onRepair}
          disabled={repairPending}
          title={t("documents.repairRelationsTitle")}
        >
          <Wrench className={cn("h-4 w-4 mr-2 shrink-0", repairPending && "animate-pulse")} />
          {t("documents.repairRelations")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onAutoLink} disabled={autoBusy}>
          <Link2 className="h-4 w-4 mr-2 shrink-0" />
          {autoBusy ? t("documents.linking") : t("documents.autoLink")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenManualLink}>
          <Plus className="h-4 w-4 mr-2 shrink-0" />
          {t("documents.manualLink")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SuperfakturaDocumentsTab({
  dealId,
  dealEmail,
  anchorDocumentId,
}: SuperfakturaDocumentsTabProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateFnsLocale(i18n.language);

  const isMobile = useIsMobile();
  const [selectedDocument, setSelectedDocument] = useState<LinkedDocument | null>(null);
  const [linkerOpen, setLinkerOpen] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [expandedQuotes, setExpandedQuotes] = useState<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);

  const [zmluvaGeneratorDoc, setZmluvaGeneratorDoc] = useState<LinkedDocument | null>(null);
  const [protocolGeneratorDoc, setProtocolGeneratorDoc] = useState<LinkedDocument | null>(null);
  const [finalInvoiceGeneratorDoc, setFinalInvoiceGeneratorDoc] = useState<LinkedDocument | null>(null);
  const [protocolInstallers, setProtocolInstallers] = useState<Set<string>>(new Set());
  const [protocolDate, setProtocolDate] = useState<Date>(new Date());
  const [protocolLocation, setProtocolLocation] = useState("Okoč");
  const [deletingContractIds, setDeletingContractIds] = useState<Set<string>>(new Set());
  const [emailFromContract, setEmailFromContract] = useState<{
    contractId: string;
    documentIds: string[];
    pdfFilename: string;
  } | null>(null);

  /** Which quote to generate zálohová faktúra from when multiple quotes lack a deposit */
  const [depositGeneratorQuoteId, setDepositGeneratorQuoteId] = useState<string | null>(null);
  // Timeline: email composer for a raw SF document (not a contract bundle)
  const [sfDocEmail, setSfDocEmail] = useState<{
    type: DealDocumentTimelineDocType;
    id: string;
  } | null>(null);
  const [sfDocEmailFiles, setSfDocEmailFiles] = useState<File[] | null>(null);
  const [sfDocEmailLoading, setSfDocEmailLoading] = useState(false);
  const [printAllPending, setPrintAllPending] = useState(false);
  const [printAllProgress, setPrintAllProgress] = useState<string | null>(null);
  const printAllAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      printAllAbortRef.current?.abort();
    };
  }, []);

  const installerOptions = ["Tamás Petráš", "Péter Takács", "Máté Mezei"];

  const qc = useQueryClient();

  const formatDateTime = (value?: string | null) => {
    if (!value) return "n/a";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "n/a";
    return d.toLocaleString("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Set anchor quote mutation
  const setAnchorDocumentMutation = useMutation({
    mutationFn: async (documentId: string | null) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/anchor-document`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documentId }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || err.error || t("documents.anchorSaveFailed", "Uloženie kotvy zlyhalo."));
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
    onError: (e: Error) => {
      toast({ title: t("common.error"), description: e.message, variant: "destructive" });
    },
  });

  // Fetch deal companies (for document linker: search by contact + company emails)
  const companiesQuery = useQuery<{ companies: Array<{ id: string; name?: string; email?: string | null }> }>({
    queryKey: ["deal-companies", dealId],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/companies`), { credentials: "include" });
      if (!r.ok) return { companies: [] };
      return r.json();
    },
    enabled: !!dealId,
  });
  const companyEmails = useMemo(
    () => (companiesQuery.data?.companies ?? []).map((c) => c.email).filter((e): e is string => !!e && e.length >= 2),
    [companiesQuery.data]
  );
  const hasDealCompany = (companiesQuery.data?.companies ?? []).length > 0;

  const duplicateQuoteWithCompanyMutation = useMutation({
    mutationFn: async (sourceDocumentId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/superfaktura/duplicate-quote-with-company`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_document_id: sourceDocumentId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(
          typeof data.message === "string" ? data.message : data.error || t("documents.duplicateQuoteFailed", "Nepodarilo sa vytvoriť ponuku.")
        );
      }
      return data;
    },
    onSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("documents.duplicateQuoteCompanySuccess", "Nová cenová ponuka na firmu bola vytvorená."),
      });
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
    onError: (e: Error) => {
      toast({ title: t("common.error"), description: e.message, variant: "destructive" });
    },
  });

  // Fetch linked documents
  type DocumentsResponse = { documents: LinkedDocument[]; grouped: Record<string, LinkedDocument[]>; total: number };
  const documentsQuery = useQuery<DocumentsResponse, Error>({
    queryKey: ["deal-documents", dealId],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents`), {
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("documents.loadFailed"));
      const data = await r.json();
      return data as DocumentsResponse;
    },
    // Cache-first: show cached data immediately, refresh in background
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime in React Query v4)
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (prev) => prev,
  });

  // Delete document mutation
  const deleteDocument = useMutation({
    mutationFn: async (docId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents/${docId}`), {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("documents.deleteFailed"));
      return r.json();
    },
    onSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("documents.deleteSuccess"),
      });
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
    onError: (error: Error) => {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Extract data mutation
  const extractData = useMutation({
    mutationFn: async (docId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents/${docId}/extract`), {
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("documents.extractFailed"));
      return r.json();
    },
    onSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("documents.extractSuccess"),
      });
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
    },
    onError: (error: Error) => {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const docResponse = documentsQuery.data as DocumentsResponse | undefined;
  const documents = docResponse?.documents ?? [];
  const grouped = docResponse?.grouped ?? {};

  const latestSync = documents.reduce<string | null>((acc, doc) => {
    if (!doc.last_synced_at) return acc;
    if (!acc) return doc.last_synced_at;
    return new Date(doc.last_synced_at) > new Date(acc) ? doc.last_synced_at : acc;
  }, null);

  // Fetch contracts from contracts table (for hierarchy: zmluvy under quotes)
  const contractsQuery = useQuery({
    queryKey: ["contracts", dealId],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/contracts`), {
        credentials: "include",
      });
      if (!r.ok) {
        console.warn("[SuperfakturaDocumentsTab] Failed to fetch contracts:", r.status);
        return [];
      }
      const data = await r.json();
      const contracts = data.contracts || [];
      return contracts;
    },
  });

  const contracts = contractsQuery.data || [];

  // Sorted quotes for the timeline selector — newest document_date first
  const sortedQuotes = useMemo(
    () =>
      documents
        .filter((d) => d.document_type === "quote")
        .sort((a, b) => {
          const byDate =
            new Date(b.document_date ?? 0).getTime() -
            new Date(a.document_date ?? 0).getTime();
          if (byDate !== 0) return byDate;
          return (b.document_number ?? "").localeCompare(a.document_number ?? "");
        }),
    [documents],
  );

  const timelineActiveQuoteId = useMemo(
    () => resolveTimelineActiveQuoteId(sortedQuotes, documents, contracts),
    [sortedQuotes, documents, contracts],
  );

  // When anchor is set and found in documents, use it as selectedQuote.
  // Otherwise fall back to heuristic resolveTimelineActiveQuoteId result.
  const selectedQuote = useMemo(() => {
    if (anchorDocumentId) {
      const anchor = sortedQuotes.find((q) => q.id === anchorDocumentId);
      if (anchor) return anchor;
    }
    return timelineActiveQuoteId
      ? sortedQuotes.find((q) => q.id === timelineActiveQuoteId) ?? null
      : null;
  }, [anchorDocumentId, sortedQuotes, timelineActiveQuoteId]);

  // Anchor quote: always the quote behind the most recently created zálohová faktúra
  // when one exists; otherwise single-quote / single-chain heuristics; else newest quote.
  const desiredAnchorDocumentId = useMemo(() => {
    if (sortedQuotes.length === 0) return null;
    const fromLatestDeposit = resolveQuoteIdFromLatestDeposit(
      sortedQuotes as RelationDoc[],
      documents as RelationDoc[],
    );
    if (fromLatestDeposit) return fromLatestDeposit;
    if (sortedQuotes.length === 1) return sortedQuotes[0].id;
    const quotesWithChain = sortedQuotes.filter(
      (q) => documentLifecycle(q as any, documents as any, contracts as any).hasDeposit,
    );
    if (quotesWithChain.length === 1) return quotesWithChain[0].id;
    return sortedQuotes[0].id;
  }, [sortedQuotes, documents, contracts]);

  useEffect(() => {
    if (documentsQuery.isLoading || contractsQuery.isLoading) return;
    if (!desiredAnchorDocumentId) return;
    if (desiredAnchorDocumentId === anchorDocumentId) return;
    if (setAnchorDocumentMutation.isPending) return;
    setAnchorDocumentMutation.mutate(desiredAnchorDocumentId);
  }, [
    anchorDocumentId,
    contractsQuery.isLoading,
    desiredAnchorDocumentId,
    documentsQuery.isLoading,
    setAnchorDocumentMutation,
  ]);

  // Quotes that can still receive a zálohová faktúra.
  // Once the kotva (selected/anchor) quote already has a zálohová, do not offer
  // generating another deposit from the remaining (usually declined) quotes.
  const quotesWithoutDeposit = useMemo(() => {
    if (selectedQuote) {
      const kotvaHasDeposit = quoteHasDepositInvoice(selectedQuote, documents);
      if (kotvaHasDeposit) return [];
    }
    return listQuotesWithoutDeposit(sortedQuotes, documents);
  }, [sortedQuotes, documents, selectedQuote]);

  const quoteForDepositGenerator = useMemo(() => {
    if (quotesWithoutDeposit.length === 0) return null;
    if (quotesWithoutDeposit.length === 1) return quotesWithoutDeposit[0];
    const preferred =
      depositGeneratorQuoteId &&
      quotesWithoutDeposit.some((q) => q.id === depositGeneratorQuoteId)
        ? depositGeneratorQuoteId
        : timelineActiveQuoteId &&
            quotesWithoutDeposit.some((q) => q.id === timelineActiveQuoteId)
          ? timelineActiveQuoteId
          : quotesWithoutDeposit[0].id;
    return sortedQuotes.find((q) => q.id === preferred) ?? quotesWithoutDeposit[0];
  }, [
    quotesWithoutDeposit,
    depositGeneratorQuoteId,
    timelineActiveQuoteId,
    sortedQuotes,
  ]);

  useEffect(() => {
    if (quotesWithoutDeposit.length <= 1) {
      setDepositGeneratorQuoteId(null);
      return;
    }
    setDepositGeneratorQuoteId((prev) => {
      if (prev && quotesWithoutDeposit.some((q) => q.id === prev)) return prev;
      const fallback =
        timelineActiveQuoteId &&
        quotesWithoutDeposit.some((q) => q.id === timelineActiveQuoteId)
          ? timelineActiveQuoteId
          : quotesWithoutDeposit[0].id;
      return fallback;
    });
  }, [quotesWithoutDeposit, timelineActiveQuoteId]);

  // Timeline data for the selected quote
  const timelineData = useMemo(() => {
    if (!selectedQuote) return null;
    return mapTimelineData(selectedQuote, documents, contracts);
  }, [selectedQuote, documents, contracts]);

  const handlePrintAllChain = useCallback(async () => {
    if (!timelineData || printAllPending) return;
    const quoteId =
      timelineData.zalohovaFaktura?.generatedFromQuoteId ||
      selectedQuote?.id ||
      timelineActiveQuoteId;
    const {
      zalohovaFaktura,
      zmluva,
      danovyDoklad,
      konecnaFaktura,
      odovzdavaciProtokol,
    } = timelineData;
    if (
      !quoteId ||
      !zalohovaFaktura ||
      !zmluva ||
      !danovyDoklad ||
      !konecnaFaktura ||
      !odovzdavaciProtokol
    ) {
      toast({
        title: t("common.error"),
        description: t(
          "documents.printAllChainDisabledHint",
          "Mind a 6 dokumentumnak léteznie kell (árajánlat, záloha, zmluva, daňový doklad, faktúra, protokol).",
        ),
        variant: "destructive",
      });
      return;
    }

    const fetchSfPdf = async (docId: string, label: string): Promise<Blob> => {
      const res = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents/${docId}/download`), {
        credentials: "include",
      });
      if (!res.ok) throw new Error(label);
      const blob = await res.blob();
      if (!blob.size) throw new Error(label);
      return blob;
    };

    const fetchContractPdf = async (contractId: string, label: string): Promise<Blob> => {
      const res = await fetch(apiUrl(`/api/crm/deals/${dealId}/contracts/${contractId}`), {
        credentials: "include",
      });
      if (!res.ok) throw new Error(label);
      const blob = await res.blob();
      if (!blob.size) throw new Error(label);
      return blob;
    };

    printAllAbortRef.current?.abort();
    const ac = new AbortController();
    printAllAbortRef.current = ac;
    setPrintAllPending(true);
    setPrintAllProgress(
      t("documents.printAllChainFetching", "PDF-ek betöltése… {{current}}/{{total}}", {
        current: 0,
        total: 6,
      }),
    );

    try {
      await printPdfBlobs(
        [
          {
            label: t("contracts.quote", "Cenová ponuka"),
            fetchBlob: () => fetchSfPdf(quoteId, t("contracts.quote", "Cenová ponuka")),
          },
          {
            label: "Zálohová faktúra",
            fetchBlob: () => fetchSfPdf(zalohovaFaktura.id, "Zálohová faktúra"),
          },
          {
            label: "Zmluva o dielo",
            fetchBlob: () => fetchContractPdf(zmluva.id, "Zmluva o dielo"),
          },
          {
            label: "Daňový doklad",
            fetchBlob: () => fetchSfPdf(danovyDoklad.id, "Daňový doklad"),
          },
          {
            label: "Konečná faktúra",
            fetchBlob: () => fetchSfPdf(konecnaFaktura.id, "Konečná faktúra"),
          },
          {
            label: "Odovzdávací protokol",
            fetchBlob: () => fetchContractPdf(odovzdavaciProtokol.id, "Odovzdávací protokol"),
          },
        ],
        {
          signal: ac.signal,
          // Chrome PDF iframe often skips afterprint; 12s safety net (not 45s).
          afterPrintTimeoutMs: 12_000,
          onProgress: (phase, current, total) => {
            if (phase === "fetch") {
              setPrintAllProgress(
                t("documents.printAllChainFetching", "PDF-ek betöltése… {{current}}/{{total}}", {
                  current,
                  total,
                }),
              );
            } else {
              setPrintAllProgress(
                t("documents.printAllChainPrinting", "Nyomtatás… {{current}}/{{total}}", {
                  current,
                  total,
                }),
              );
            }
          },
        },
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const msg = err instanceof Error ? err.message : String(err);
      toast({
        title: t("common.error"),
        description: t(
          "documents.printAllChainFailed",
          "Nem sikerült kinyomtatni az összes dokumentumot: {{error}}",
          { error: msg },
        ),
        variant: "destructive",
      });
    } finally {
      setPrintAllPending(false);
      setPrintAllProgress(null);
      if (printAllAbortRef.current === ac) printAllAbortRef.current = null;
    }
  }, [
    dealId,
    printAllPending,
    selectedQuote?.id,
    t,
    timelineActiveQuoteId,
    timelineData,
  ]);

  // Lifecycle for the selected quote (for the "Ďalší krok" panel)
  const selectedLifecycle = useMemo(() => {
    if (!selectedQuote) return null;
    return documentLifecycle(
      selectedQuote as any,
      documents as any,
      contracts as any,
    );
  }, [selectedQuote, documents, contracts]);

  // Deposit document for the selected quote (needed by QuoteAdvanceGenerator)
  const selectedDeposit = useMemo(() => {
    if (!selectedQuote) return undefined;
    return documents.find(
      (d) =>
        (d.document_type === "deposit_invoice" || d.document_type === "proforma") &&
        isDocumentRelatedToQuote(d, selectedQuote.id, selectedQuote.document_number ?? selectedQuote.sf_document_id, selectedQuote.sf_document_id),
    );
  }, [selectedQuote, documents]);

  // Tax document for the selected quote (needed by "Ďalší krok" button)
  const selectedTaxDoc = useMemo(() => {
    if (!selectedQuote || !selectedDeposit) return undefined;
    return documents.find(
      (d) =>
        d.document_type === "tax_document" &&
        isDocumentRelatedToDocument(d, selectedDeposit.id, selectedDeposit),
    );
  }, [selectedQuote, selectedDeposit, documents]);

  // Final invoice for the selected quote (for protocol-only button)
  const selectedFinalInvoice = useMemo(() => {
    if (!selectedQuote) return undefined;
    return documents.find(
      (d) =>
        d.document_type === "invoice" &&
        (isDocumentRelatedToQuote(d, selectedQuote.id, selectedQuote.document_number ?? selectedQuote.sf_document_id, selectedQuote.sf_document_id) ||
          (selectedDeposit && isDocumentRelatedToDocument(d, selectedDeposit.id, selectedDeposit))),
    );
  }, [selectedQuote, selectedDeposit, documents]);

  // Helper function to check if quote is "Schválená" (approved)
  const isQuoteApproved = (doc: LinkedDocument): boolean => {
    if (doc.document_type !== "quote") return false;
    
    // Get status from multiple sources
    const status = (doc.status || "").toLowerCase();
    const rawStatusRaw = (doc as any).raw_data?.Invoice?.status || (doc as any).raw_data?.Quote?.status;
    const rawStatus = rawStatusRaw ? String(rawStatusRaw).toLowerCase() : "";
    const statusId = (doc as any).status_id || (doc as any).raw_data?.Invoice?.status_id || (doc as any).raw_data?.Quote?.status_id;

    // Check various possible status values for "approved" / "Schválená"
    // Superfaktura uses status "2" or "Schválená" for approved quotes
    const isApproved = (
      status === "approved" ||
      status === "schválená" ||
      status === "schvalena" ||
      status === "accepted" ||
      status === "confirmed" ||
      rawStatus === "approved" ||
      rawStatus === "schválená" ||
      rawStatus === "schvalena" ||
      rawStatus === "accepted" ||
      rawStatus === "confirmed" ||
      rawStatus === "2" || // Superfaktura status code "2" = "Schválená"
      // Check if status_id indicates approved (status_id 2 = "Schválená" in Superfaktura)
      statusId === 2 ||
      statusId === 3 ||
      // Also check string versions
      String(statusId) === "2" ||
      String(statusId) === "3" ||
      String(rawStatusRaw) === "2" ||
      String(rawStatusRaw) === "3"
    );
    
    return isApproved;
  };

  // Auto-link documents mutation
  const autoLinkDocuments = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/auto-link-documents`), {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const error = await r.json().catch(() => ({}));
        throw new Error(
          typeof error.message === "string"
            ? error.message
            : typeof error.error === "string"
              ? error.error
              : t("documents.autoLinkFailed"),
        );
      }
      return r.json();
    },
    onSuccess: async (data) => {
      const linked = Number(data.linked) || 0;
      const remaining = Number(data.documentsRemaining) || 0;
      const partial = Boolean(data.partial);
      await qc.refetchQueries({ queryKey: ["deal-documents", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      if (linked === 0 && !partial) {
        toast({
          title: t("common.success"),
          description: t("documents.autoLinkNoNew"),
        });
      } else {
        toast({
          title: t("common.success"),
          description: partial
            ? t("documents.autoLinkPartial", { count: linked, remaining })
            : `${linked} ${t("documents.autoLink").toLowerCase()}`,
        });
      }
      setAutoLinking(false);
    },
    onError: (error: Error) => {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
      setAutoLinking(false);
    },
  });

  // Sync documents mutation
  const syncDocuments = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents/sync`), {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const error = await r.json();
        throw new Error(error.message || t("documents.syncFailed"));
      }
      return r.json();
    },
    onSuccess: (data) => {
      const count = (data.synced || 0) + (data.updated || 0);
      toast({
        title: t("common.success"),
        description: count > 0
          ? `${data.synced || 0} ${t("documents.sync")}, ${data.updated || 0}. ${t("documents.saveCompanyHint", "Dokumentumok IČO-val: Mentés cégként gomb.")}`
          : t("documents.syncNothingToUpdate"),
      });
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
      setSyncing(false);
    },
    onError: (error: Error) => {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
      setSyncing(false);
    },
  });

  const generateContractMutation = useMutation({
    mutationFn: async ({ templateId, documentIds, installerNames, installerDate, location, contractDate }: {
      templateId: string;
      documentIds?: string[];
      installerNames?: string;
      installerDate?: string;
      location?: string;
      contractDate?: string;
    }) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/contracts`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ templateId, documentIds, installerNames, installerDate, location, contractDate }),
      });
      if (!r.ok) {
        const error = await r.json().catch(() => ({ error: "unknown_error" }));
        throw new Error(error.error || error.message || t("contracts.generateFailed"));
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: t("common.success"), description: t("contracts.generateSuccess") });
      setZmluvaGeneratorDoc(null);
      setProtocolGeneratorDoc(null);
      setProtocolInstallers(new Set());
      setProtocolDate(new Date());
      setProtocolLocation("Okoč");
      qc.invalidateQueries({ queryKey: ["contracts", dealId] });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  /** Same payload as the Zmluva confirmation dialog — used to skip the dialog when an anchor quote is set. */
  const generateZmluvaFromDeposit = useCallback(
    (deposit: LinkedDocument) => {
      const parentQuote = documents.find(
        (d) =>
          d.document_type === "quote" &&
          isDocumentRelatedToQuote(
            deposit,
            d.id,
            d.document_number ?? d.sf_document_id,
            d.sf_document_id,
          ),
      );
      const docIds = parentQuote ? [parentQuote.id, deposit.id] : [deposit.id];
      generateContractMutation.mutate({ templateId: "zmluva-o-dielo", documentIds: docIds });
    },
    [documents, generateContractMutation],
  );

  const requestZmluvaGeneration = useCallback(
    (deposit: LinkedDocument) => {
      if (anchorDocumentId) {
        generateZmluvaFromDeposit(deposit);
      } else {
        setZmluvaGeneratorDoc(deposit);
      }
    },
    [anchorDocumentId, generateZmluvaFromDeposit],
  );

  // Tracks the structured conflict / ambiguous responses from create-final-invoice
  // so the dialog can offer "Generovať len protokol" or a quote-pick dropdown.
  type FinalInvoiceConflict =
    | {
        kind: "final_exists";
        existing: {
          id: string;
          document_number?: string;
          sf_document_id?: string;
          total_amount?: number;
          pdf_url?: string;
        };
      }
    | {
        kind: "ambiguous_quote";
        candidates: Array<{
          id: string;
          document_number?: string;
          sf_document_id?: string;
          total_amount?: number;
          document_date?: string;
        }>;
      };
  const [finalInvoiceConflict, setFinalInvoiceConflict] = useState<FinalInvoiceConflict | null>(null);

  const createFinalInvoiceMutation = useMutation({
    mutationFn: async ({
      taxDocumentId,
      installerNames,
      installerDate,
      location,
      force,
      explicitQuoteId,
    }: {
      taxDocumentId: string;
      installerNames?: string;
      installerDate?: string;
      location?: string;
      force?: boolean;
      explicitQuoteId?: string;
    }) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/documents/create-final-invoice`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ taxDocumentId, installerNames, installerDate, location, force, explicitQuoteId }),
      });
      if (r.status === 409) {
        const err = await r.json().catch(() => ({}));
        if (err?.error === "final_invoice_exists" && err.existing) {
          setFinalInvoiceConflict({ kind: "final_exists", existing: err.existing });
          throw new Error(err.message || t("documents.finalInvoiceExists", "A Konečná faktúra már létezik."));
        }
      }
      if (r.status === 400) {
        const err = await r.json().catch(() => ({}));
        if (err?.error === "ambiguous_quote_link" && Array.isArray(err.candidates)) {
          setFinalInvoiceConflict({ kind: "ambiguous_quote", candidates: err.candidates });
          throw new Error(err.message || t("documents.ambiguousQuote", "Több quote is illik a Zálohová faktúrára."));
        }
      }
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || err.error || t("documents.createFinalInvoiceFailed"));
      }
      const contentType = r.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const data = await r.json();
        return { blob: null, filename: null, jsonSuccess: data };
      }
      const invoiceNumber = r.headers.get("X-Final-Invoice-Number") || "";
      const isInvoiceFallback = r.headers.get("X-Document-Type") === "invoice";
      const filename = isInvoiceFallback
        ? (invoiceNumber ? `Konecna_faktura_${invoiceNumber}.pdf` : "Konecna_faktura.pdf")
        : (invoiceNumber ? `Odovzdavaci_protokol_${invoiceNumber}.pdf` : "Odovzdavaci_protokol.pdf");
      const blob = await r.blob();
      return { blob, filename };
    },
    onSuccess: ({ blob, filename, jsonSuccess }) => {
      if (jsonSuccess) {
        toast({ title: t("common.success"), description: jsonSuccess.message || t("documents.finalInvoiceAndProtocolSuccess") });
        setFinalInvoiceGeneratorDoc(null);
        setProtocolInstallers(new Set());
        setProtocolDate(new Date());
        setProtocolLocation("Okoč");
        qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
        qc.invalidateQueries({ queryKey: ["contracts", dealId] });
        return;
      }
      if (blob && filename) {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
      toast({ title: t("common.success"), description: t("documents.finalInvoiceAndProtocolSuccess") });
      setFinalInvoiceGeneratorDoc(null);
      setProtocolInstallers(new Set());
      setProtocolDate(new Date());
      setProtocolLocation("Okoč");
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  // Generate ONLY the Odovzdávací protokol for an existing Konečná faktúra row
  // (used by the "Generovať len protokol" button when create-final-invoice returns 409).
  const protocolForFinalMutation = useMutation({
    mutationFn: async ({
      finalInvoiceId,
      installerNames,
      installerDate,
      location,
    }: {
      finalInvoiceId: string;
      installerNames?: string;
      installerDate?: string;
      location?: string;
    }) => {
      const r = await fetch(
        apiUrl(`/api/crm/deals/${dealId}/documents/generate-protocol-for-final`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ finalInvoiceId, installerNames, installerDate, location }),
        },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || err.error || t("contracts.generateFailed"));
      }
      return r.json();
    },
    onSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("contracts.generateSuccess"),
      });
      setFinalInvoiceGeneratorDoc(null);
      setFinalInvoiceConflict(null);
      setProtocolInstallers(new Set());
      setProtocolDate(new Date());
      setProtocolLocation("Okoč");
      qc.invalidateQueries({ queryKey: ["contracts", dealId] });
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  // Walk every superfaktura_documents row in this deal and fill missing
  // related_document_id values where the resolver yields a single match.
  const repairRelationsMutation = useMutation({
    mutationFn: async () => {
      const r = await fetch(
        apiUrl(`/api/crm/deals/${dealId}/documents/repair-relations`),
        { method: "POST", credentials: "include" },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || err.error || t("documents.repairRelationsFailed", "Oprava prepojení zlyhala"));
      }
      return r.json() as Promise<{
        success: boolean;
        total: number;
        alreadyLinked: number;
        updated: number;
        ambiguous: number;
        unresolved: number;
      }>;
    },
    onSuccess: (data) => {
      toast({
        title: t("common.success"),
        description: t(
          "documents.repairRelationsSummary",
          "Aktualizováno: {{updated}} • nejasné: {{ambiguous}} • nenájdené: {{unresolved}}",
          {
            updated: data.updated,
            ambiguous: data.ambiguous,
            unresolved: data.unresolved,
          },
        ),
      });
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteContractMutation = useMutation({
    mutationFn: async (contractId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/contracts/${contractId}`), {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || err.message || t("contracts.deleteFailed"));
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: t("common.success"), description: t("contracts.deleteSuccess") });
      qc.invalidateQueries({ queryKey: ["contracts", dealId] });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
    onSettled: (_, __, contractId) => {
      setDeletingContractIds((prev) => {
        const next = new Set(prev);
        next.delete(contractId);
        return next;
      });
    },
  });

  // Auto-sync once when there are documents without sync data (e.g. configurator-linked before backfill)
  const autoSyncTriggered = useRef<string | null>(null);
  useEffect(() => {
    if (documentsQuery.isLoading || documentsQuery.isError) return;
    if (autoSyncTriggered.current === dealId) return;
    const docs = documentsQuery.data?.documents ?? [];
    const needsBackfill = docs.some((d: LinkedDocument) => d.sf_document_id && !d.document_number);
    if (needsBackfill && docs.length > 0) {
      autoSyncTriggered.current = dealId;
      setSyncing(true);
      syncDocuments.mutate(undefined, {
        onSettled: () => setSyncing(false),
      });
    }
  }, [dealId, documentsQuery.data, documentsQuery.isLoading, documentsQuery.isError]);

  // Fetch PDF for SF-doc email when sfDocEmail is set
  useEffect(() => {
    if (!sfDocEmail) {
      setSfDocEmailFiles(null);
      setSfDocEmailLoading(false);
      return;
    }
    let cancelled = false;
    setSfDocEmailLoading(true);
    setSfDocEmailFiles(null);
    (async () => {
      try {
        const url = apiUrl(`/api/crm/deals/${dealId}/documents/${sfDocEmail.id}/download`);
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(t("documents.downloadFailed"));
        const blob = await res.blob();
        const doc = documents.find((d) => d.id === sfDocEmail.id);
        const filename = `${doc?.document_number ?? doc?.document_name ?? sfDocEmail.id}.pdf`;
        if (!cancelled) setSfDocEmailFiles([new File([blob], filename, { type: "application/pdf" })]);
      } catch {
        if (!cancelled) setSfDocEmail(null);
      } finally {
        if (!cancelled) setSfDocEmailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sfDocEmail?.id, dealId]);

  const handleAutoLink = () => {
    setAutoLinking(true);
    autoLinkDocuments.mutate();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">{t("documents.title")}</h3>
          <div className="flex items-center gap-2 text-sm text-foreground/60">
            <span>{t("documents.linkedCount", { count: documents.length })}</span>
            <span className="text-xs text-foreground/50" title={latestSync ? t("documents.lastSync", { date: formatDateTime(latestSync) }) : t("documents.noSyncYet")}>
              {documentsQuery.isFetching
                ? t("documents.refreshing")
                : latestSync
                  ? t("documents.lastSync", { date: formatDateTime(latestSync) })
                  : t("documents.noSyncYet")}
            </span>
          </div>
        </div>
        <div className="flex shrink-0">
          <DealDocumentsActionsMenu
            t={t}
            align="end"
            syncing={syncing}
            syncPending={syncDocuments.isPending}
            onSync={() => {
              setSyncing(true);
              syncDocuments.mutate();
            }}
            repairPending={repairRelationsMutation.isPending}
            onRepair={() => repairRelationsMutation.mutate()}
            autoLinking={autoLinking}
            autoLinkPending={autoLinkDocuments.isPending}
            onAutoLink={handleAutoLink}
            onOpenManualLink={() => setLinkerOpen(true)}
          />
        </div>
      </div>

      {/* Loading – wait for both documents and contracts so contract rows don't appear with delay */}
      {(documentsQuery.isLoading || contractsQuery.isLoading) && (
        <div className="text-center py-8 text-foreground/60">{t("common.loading")}</div>
      )}

      {/* Error */}
      {documentsQuery.isError && (
        <div className="text-center py-8 text-red-600">
          {t("documents.loadFailed")}
        </div>
      )}

      {/* Empty State – only when both queries loaded and no rows to show */}
      {!documentsQuery.isLoading && !contractsQuery.isLoading && documents.length === 0 && (
        <div className="text-center py-12 border rounded-lg">
          <p className="text-foreground/60 mb-4">{t("documents.noLinkedDocuments")}</p>
          <div className="flex justify-center">
            <DealDocumentsActionsMenu
              t={t}
              align="center"
              syncing={syncing}
              syncPending={syncDocuments.isPending}
              onSync={() => {
                setSyncing(true);
                syncDocuments.mutate();
              }}
              repairPending={repairRelationsMutation.isPending}
              onRepair={() => repairRelationsMutation.mutate()}
              autoLinking={autoLinking}
              autoLinkPending={autoLinkDocuments.isPending}
              onAutoLink={handleAutoLink}
              onOpenManualLink={() => setLinkerOpen(true)}
            />
          </div>
        </div>
      )}

      {/* Document Timeline */}
      {!documentsQuery.isLoading && !contractsQuery.isLoading && documents.length > 0 && (
        <div className="space-y-4">
          {/* Timeline stepper — active quote inferred from document chain (no top dropdown) */}
          {timelineData && (
            <DealDocumentTimeline
              {...timelineData}
              offers={timelineData.offers}
              activeOfferQuoteId={timelineActiveQuoteId}
              anchorQuoteId={anchorDocumentId ?? null}
              onSetAnchor={(id) => setAnchorDocumentMutation.mutate(id)}
              onChangeAnchor={() => setAnchorDocumentMutation.mutate(null)}
              onDocumentOpen={(type, id) => {
                if (type === "contract_zmluva" || type === "contract_protocol") {
                  window.open(apiUrl(`/api/crm/deals/${dealId}/contracts/${id}?inline=1`), "_blank");
                } else {
                  const doc = documents.find((d) => d.id === id);
                  if (doc) setSelectedDocument(doc);
                }
              }}
              onDocumentDownload={async (type, id) => {
                if (type === "contract_zmluva" || type === "contract_protocol") {
                  try {
                    const url = apiUrl(`/api/crm/deals/${dealId}/contracts/${id}`);
                    const res = await fetch(url, { credentials: "include" });
                    if (!res.ok) throw new Error(t("documents.downloadFailed"));
                    const blob = await res.blob();
                    const contract = contracts.find((c) => c.id === id);
                    const filename = (contract as any)?.pdf_filename ?? `zmluva_${id}.pdf`;
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = blobUrl;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(blobUrl);
                    toast({ title: t("common.success"), description: t("documents.downloadSuccess") });
                  } catch {
                    toast({ title: t("common.error"), description: t("documents.downloadFailed"), variant: "destructive" });
                  }
                } else {
                  setDownloadingIds(new Set([id]));
                  try {
                    const url = apiUrl(`/api/crm/deals/${dealId}/documents/${id}/download`);
                    const res = await fetch(url, { credentials: "include" });
                    if (!res.ok) throw new Error(t("documents.downloadFailed"));
                    const blob = await res.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = blobUrl;
                    const cd = res.headers.get("Content-Disposition");
                    const fnMatch = cd?.match(/filename="(.+)"/);
                    const doc = documents.find((d) => d.id === id);
                    a.download = fnMatch?.[1] ?? `${doc?.document_number ?? id}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(blobUrl);
                    toast({ title: t("common.success"), description: t("documents.downloadSuccess") });
                  } catch {
                    toast({ title: t("common.error"), description: t("documents.downloadFailed"), variant: "destructive" });
                  } finally {
                    setDownloadingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
                  }
                }
              }}
              onDocumentEmail={(type, id) => {
                if (type === "contract_zmluva" || type === "contract_protocol") {
                  const contract = contracts.find((c) => c.id === id);
                  if (contract) {
                    setEmailFromContract({
                      contractId: id,
                      documentIds: (contract as any).document_ids ?? [],
                      pdfFilename: (contract as any).pdf_filename ?? "Zmluva.pdf",
                    });
                  }
                } else {
                  setSfDocEmail({ type, id });
                }
              }}
              onDocumentDelete={(type, id) => {
                const label =
                  type === "contract_zmluva" || type === "contract_protocol"
                    ? t("contractsExtra.deleteContractConfirm")
                    : t("documents.deleteConfirm");
                if (confirm(label)) {
                  if (type === "contract_zmluva" || type === "contract_protocol") {
                    setDeletingContractIds((prev) => new Set(prev).add(id));
                    deleteContractMutation.mutate(id);
                  } else {
                    setDeletingIds(new Set([id]));
                    deleteDocument.mutate(id, {
                      onSettled: () =>
                        setDeletingIds((prev) => { const n = new Set(prev); n.delete(id); return n; }),
                    });
                  }
                }
              }}
              onOfferDuplicate={(id) => {
                if (!hasDealCompany) {
                  toast({
                    title: t("common.error"),
                    description: t("deal.addCompanyFirstShort", "Najprv pridajte firmu v Spoločnostiach."),
                    variant: "destructive",
                  });
                  return;
                }
                duplicateQuoteWithCompanyMutation.mutate(id);
              }}
              onGenerateZmluva={
                selectedDeposit && selectedLifecycle && !selectedLifecycle.hasContract
                  ? () => requestZmluvaGeneration(selectedDeposit)
                  : undefined
              }
              nextStepSlot={(() => {
                const kotvaHasDeposit = !!selectedLifecycle?.hasDeposit;
                // Only offer a new zálohová when the kotva chain does not already have one.
                const showDepositSection =
                  !kotvaHasDeposit &&
                  quotesWithoutDeposit.length > 0 &&
                  quoteForDepositGenerator != null;
                const showZmluvaAction =
                  !!selectedDeposit && !!selectedLifecycle && !selectedLifecycle.hasContract;
                const showFinalAction =
                  !!selectedTaxDoc &&
                  !!selectedLifecycle &&
                  (selectedLifecycle.nextStep === "create_final_invoice" ||
                    selectedLifecycle.nextStep === "generate_protocol_only" ||
                    (selectedLifecycle.hasTaxDoc && !selectedLifecycle.hasFinal));
                const showAwaitTaxHint =
                  !!selectedDeposit &&
                  !!selectedLifecycle &&
                  selectedLifecycle.hasDeposit &&
                  !selectedLifecycle.hasTaxDoc;
                if (!showDepositSection && !showZmluvaAction && !showFinalAction && !showAwaitTaxHint)
                  return null;
                return (
                  <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {t("documents.nextStep", "Ďalší krok")}
                    </div>
                    {showDepositSection && quoteForDepositGenerator && (
                      <div className="space-y-2">
                        {quotesWithoutDeposit.length > 1 && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">
                              {t(
                                "documents.pickQuoteForDeposit",
                                "Vyberte cenovú ponuku, z ktorej sa má vygenerovať zálohová faktúra:",
                              )}
                            </p>
                            <RadioGroup
                              value={
                                depositGeneratorQuoteId ??
                                quoteForDepositGenerator.id
                              }
                              onValueChange={setDepositGeneratorQuoteId}
                              className="gap-1.5"
                            >
                              {quotesWithoutDeposit.map((q) => (
                                <div
                                  key={q.id}
                                  className="flex gap-2 rounded-md border border-border/60 bg-background/50 px-2 py-1.5 items-center"
                                >
                                  <RadioGroupItem value={q.id} id={`dep-quote-${q.id}`} />
                                  <Label
                                    htmlFor={`dep-quote-${q.id}`}
                                    className="flex-1 cursor-pointer text-xs font-normal leading-tight"
                                  >
                                    <span className="font-medium">
                                      {q.document_number ?? q.document_name ?? q.id}
                                    </span>
                                    {q.total_amount != null && (
                                      <span className="text-muted-foreground">
                                        {" "}
                                        ·{" "}
                                        {Number(q.total_amount).toFixed(2).replace(".", ",")} €
                                      </span>
                                    )}
                                  </Label>
                                </div>
                              ))}
                            </RadioGroup>
                          </div>
                        )}
                        <QuoteAdvanceGenerator
                          dealId={dealId}
                          quoteDocument={{
                            id: quoteForDepositGenerator.id,
                            document_type: quoteForDepositGenerator.document_type ?? "quote",
                            document_number: quoteForDepositGenerator.document_number ?? undefined,
                            document_name: quoteForDepositGenerator.document_name ?? undefined,
                            total_amount: quoteForDepositGenerator.total_amount ?? undefined,
                            currency: quoteForDepositGenerator.currency ?? undefined,
                          }}
                          existingDepositInvoice={undefined}
                          existingContract={undefined}
                        />
                      </div>
                    )}
                    {(showZmluvaAction || showFinalAction || showAwaitTaxHint) && (
                      <div className="flex flex-col gap-2">
                        {showZmluvaAction && selectedDeposit && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="justify-start"
                            onClick={() => requestZmluvaGeneration(selectedDeposit)}
                            disabled={generateContractMutation.isPending}
                          >
                            <FileSignature className="h-4 w-4 mr-2" />
                            {t("contracts.generateZmluva")}
                          </Button>
                        )}
                        {showFinalAction && selectedTaxDoc && selectedLifecycle && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="justify-start"
                            onClick={() => {
                              setFinalInvoiceGeneratorDoc(selectedTaxDoc);
                              if (selectedLifecycle.hasFinal && selectedFinalInvoice) {
                                setFinalInvoiceConflict({
                                  kind: "final_exists",
                                  existing: {
                                    id: selectedFinalInvoice.id,
                                    document_number: selectedFinalInvoice.document_number ?? undefined,
                                    sf_document_id: selectedFinalInvoice.sf_document_id ?? undefined,
                                    total_amount: selectedFinalInvoice.total_amount ?? undefined,
                                    pdf_url: selectedFinalInvoice.pdf_url ?? undefined,
                                  },
                                });
                              } else {
                                setFinalInvoiceConflict(null);
                              }
                            }}
                            disabled={
                              createFinalInvoiceMutation.isPending || protocolForFinalMutation.isPending
                            }
                          >
                            {selectedLifecycle.hasFinal ||
                            selectedLifecycle.nextStep === "generate_protocol_only" ? (
                              <>
                                <ClipboardCheck className="h-4 w-4 mr-2" />
                                {t("contracts.generateProtocol")}
                              </>
                            ) : (
                              <>
                                <Receipt className="h-4 w-4 mr-2" />
                                {t("contracts.generateFinalInvoice")}
                              </>
                            )}
                          </Button>
                        )}
                        {showAwaitTaxHint && (
                          <span className="text-xs text-muted-foreground">
                            {t("documents.awaitTaxDoc", "Čakáme na Daňový doklad k prijatej platbe")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              onPrintAllChain={handlePrintAllChain}
              printAllPending={printAllPending}
              printAllProgressLabel={printAllProgress}
            />
          )}

        </div>
      )}

      {/* Document Details Modal */}
      {selectedDocument && (
        <DocumentDetails
          document={selectedDocument}
          isOpen={!!selectedDocument}
          onClose={() => setSelectedDocument(null)}
          dealId={dealId}
        />
      )}

      {/* Document Linker Modal */}
      <DocumentLinker
        dealId={dealId}
        dealEmail={dealEmail}
        companyEmails={companyEmails}
        isOpen={linkerOpen}
        onClose={() => setLinkerOpen(false)}
        onSuccess={() => documentsQuery.refetch()}
      />

      {/* Zmluva o dielo generation dialog (simple) */}
      <Dialog open={!!zmluvaGeneratorDoc} onOpenChange={(open) => { if (!open) setZmluvaGeneratorDoc(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("contracts.generateZmluvaTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-foreground/60">{t("contracts.generateZmluvaDesc")}</p>
            {zmluvaGeneratorDoc && (
              <div className="border rounded-lg p-3 bg-muted/30 text-sm">
                <div className="font-medium">{zmluvaGeneratorDoc.document_name || zmluvaGeneratorDoc.document_number}</div>
                <div className="text-xs text-foreground/60 mt-1">
                  {zmluvaGeneratorDoc.total_amount?.toFixed(2).replace(".", ",")} {zmluvaGeneratorDoc.currency || "EUR"}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setZmluvaGeneratorDoc(null)} disabled={generateContractMutation.isPending}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  if (!zmluvaGeneratorDoc) return;
                  generateZmluvaFromDeposit(zmluvaGeneratorDoc);
                }}
                disabled={generateContractMutation.isPending}
              >
                {generateContractMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("contracts.generating")}</>
                ) : (
                  <><FileSignature className="h-4 w-4 mr-2" />{t("contracts.generateAndSave")}</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Konečná faktúra + Protokol generation dialog (from Daňový doklad) */}
      <Dialog open={!!finalInvoiceGeneratorDoc} onOpenChange={(open) => {
        if (!open) {
          setFinalInvoiceGeneratorDoc(null);
          setFinalInvoiceConflict(null);
          setProtocolInstallers(new Set());
          setProtocolDate(new Date());
          setProtocolLocation("Okoč");
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("contracts.generateFinalInvoiceTitle")}</DialogTitle>
            <DialogDescription>{t("contracts.generateFinalInvoiceDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {finalInvoiceGeneratorDoc && (
              <div className="border rounded-lg p-3 bg-muted/30 text-sm">
                <div className="font-medium">{finalInvoiceGeneratorDoc.document_name || finalInvoiceGeneratorDoc.document_number}</div>
                <div className="text-xs text-foreground/60 mt-1">
                  {finalInvoiceGeneratorDoc.total_amount?.toFixed(2).replace(".", ",")} {finalInvoiceGeneratorDoc.currency || "EUR"}
                </div>
              </div>
            )}

            {/* Idempotency: a Konečná already exists → offer to generate just the protocol. */}
            {finalInvoiceConflict?.kind === "final_exists" && (
              <div className="border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <div className="font-medium">
                      {t(
                        "documents.finalInvoiceExistsTitle",
                        "Konečná faktúra už existuje",
                      )}{" "}
                      {finalInvoiceConflict.existing.document_number}
                    </div>
                    <div className="text-xs text-foreground/70">
                      {t(
                        "documents.finalInvoiceExistsHint",
                        "K tomuto cenovému ponuke je už vytvorená Konečná faktúra. Môžete vygenerovať len Odovzdávací protokol, alebo vytvoriť ešte ďalšiu Konečnú.",
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ambiguous quote: user has to pick which Cenová ponuka. */}
            {finalInvoiceConflict?.kind === "ambiguous_quote" && (
              <div className="border-2 border-blue-300 bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2 flex-1">
                    <div className="font-medium">
                      {t("documents.ambiguousQuoteTitle", "Vyberte cenovú ponuku")}
                    </div>
                    <div className="text-xs text-foreground/70">
                      {t(
                        "documents.ambiguousQuoteHint",
                        "Zálohová faktúra zodpovedá viacerým cenovým ponukám. Vyberte správnu.",
                      )}
                    </div>
                    <div className="space-y-1.5 pt-1">
                      {finalInvoiceConflict.candidates.map((c) => (
                        <Button
                          key={c.id}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          disabled={createFinalInvoiceMutation.isPending}
                          onClick={() => {
                            if (!finalInvoiceGeneratorDoc) return;
                            const installerNamesStr =
                              protocolInstallers.size > 0
                                ? Array.from(protocolInstallers).join(" a ")
                                : undefined;
                            const dateStr = protocolDate
                              ? format(protocolDate, "dd.MM.yyyy", { locale: dateLocale })
                              : undefined;
                            setFinalInvoiceConflict(null);
                            createFinalInvoiceMutation.mutate({
                              taxDocumentId: finalInvoiceGeneratorDoc.id,
                              installerNames: installerNamesStr,
                              installerDate: dateStr,
                              location: protocolLocation || undefined,
                              explicitQuoteId: c.id,
                            });
                          }}
                        >
                          <FileSignature className="h-4 w-4 mr-2" />
                          <span className="font-medium">{c.document_number || c.sf_document_id}</span>
                          {c.total_amount != null && (
                            <span className="ml-auto text-xs text-foreground/60">
                              {Number(c.total_amount).toFixed(2).replace(".", ",")} EUR
                            </span>
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("contracts.installerNames")}</Label>
                  <div className="border rounded-lg p-3 space-y-2">
                    {installerOptions.map((installer) => (
                      <div key={installer} className="flex items-center space-x-2">
                        <Checkbox
                          id={`final-installer-${installer}`}
                          checked={protocolInstallers.has(installer)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(protocolInstallers);
                            if (checked) newSet.add(installer);
                            else newSet.delete(installer);
                            setProtocolInstallers(newSet);
                          }}
                        />
                        <label htmlFor={`final-installer-${installer}`} className="text-sm cursor-pointer flex-1">
                          {installer}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="final-protocol-date">{t("contracts.installerDate")}</Label>
                    <DatePickerPopover
                      id="final-protocol-date"
                      value={protocolDate ? format(protocolDate, "yyyy-MM-dd") : ""}
                      onChange={(v) => {
                        if (v) setProtocolDate(new Date(v + "T00:00:00"));
                      }}
                      placeholder={t("contracts.selectDate")}
                      locale={dateLocale}
                      clearable={false}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="final-location">{t("contracts.locationLabel")}</Label>
                    <Input
                      id="final-location"
                      value={protocolLocation}
                      onChange={(e) => setProtocolLocation(e.target.value)}
                      placeholder={t("contracts.locationPlaceholder")}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setFinalInvoiceGeneratorDoc(null);
                  setFinalInvoiceConflict(null);
                }}
                disabled={
                  createFinalInvoiceMutation.isPending || protocolForFinalMutation.isPending
                }
              >
                {t("common.cancel")}
              </Button>

              {/* If a Konečná already exists for this quote: primary action = "generate protocol only". */}
              {finalInvoiceConflict?.kind === "final_exists" && (
                <Button
                  onClick={() => {
                    if (!finalInvoiceConflict || finalInvoiceConflict.kind !== "final_exists") return;
                    const installerNamesStr =
                      protocolInstallers.size > 0
                        ? Array.from(protocolInstallers).join(" a ")
                        : undefined;
                    const dateStr = protocolDate
                      ? format(protocolDate, "dd.MM.yyyy", { locale: dateLocale })
                      : undefined;
                    protocolForFinalMutation.mutate({
                      finalInvoiceId: finalInvoiceConflict.existing.id,
                      installerNames: installerNamesStr,
                      installerDate: dateStr,
                      location: protocolLocation || undefined,
                    });
                  }}
                  disabled={protocolForFinalMutation.isPending}
                >
                  {protocolForFinalMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("contracts.generating")}
                    </>
                  ) : (
                    <>
                      <ClipboardCheck className="h-4 w-4 mr-2" />
                      {t(
                        "contracts.generateProtocolOnly",
                        "Generovať len protokol",
                      )}
                    </>
                  )}
                </Button>
              )}

              {/* Hide the "create new" button when we're awaiting the user's quote selection. */}
              {finalInvoiceConflict?.kind !== "ambiguous_quote" && (
                <Button
                  variant={finalInvoiceConflict?.kind === "final_exists" ? "outline" : "default"}
                  onClick={() => {
                    if (!finalInvoiceGeneratorDoc) return;
                    const installerNamesStr =
                      protocolInstallers.size > 0
                        ? Array.from(protocolInstallers).join(" a ")
                        : undefined;
                    const dateStr = protocolDate
                      ? format(protocolDate, "dd.MM.yyyy", { locale: dateLocale })
                      : undefined;
                    createFinalInvoiceMutation.mutate({
                      taxDocumentId: finalInvoiceGeneratorDoc.id,
                      installerNames: installerNamesStr,
                      installerDate: dateStr,
                      location: protocolLocation || undefined,
                      // Force a new Konečná only when the user explicitly opts in via the "exists" warning state.
                      force: finalInvoiceConflict?.kind === "final_exists",
                    });
                  }}
                  disabled={createFinalInvoiceMutation.isPending}
                >
                  {createFinalInvoiceMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("contracts.generating")}
                    </>
                  ) : finalInvoiceConflict?.kind === "final_exists" ? (
                    <>
                      <Receipt className="h-4 w-4 mr-2" />
                      {t(
                        "contracts.generateFinalInvoiceForce",
                        "Vytvoriť ďalšiu Konečnú",
                      )}
                    </>
                  ) : (
                    <>
                      <Receipt className="h-4 w-4 mr-2" />
                      {t("contracts.generateFinalInvoiceAndProtocol")}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Protokol generation dialog (Kiegészítő információk style) */}
      <Dialog open={!!protocolGeneratorDoc} onOpenChange={(open) => {
        if (!open) {
          setProtocolGeneratorDoc(null);
          setProtocolInstallers(new Set());
          setProtocolDate(new Date());
          setProtocolLocation("Okoč");
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("contracts.generateProtocolTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-sm text-foreground/60">{t("contracts.generateProtocolDesc")}</p>

            {protocolGeneratorDoc && (
              <div className="border rounded-lg p-3 bg-muted/30 text-sm">
                <div className="font-medium">{protocolGeneratorDoc.document_name || protocolGeneratorDoc.document_number}</div>
                <div className="text-xs text-foreground/60 mt-1">
                  {protocolGeneratorDoc.total_amount?.toFixed(2).replace(".", ",")} {protocolGeneratorDoc.currency || "EUR"}
                </div>
              </div>
            )}

            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("contracts.installerNames")}</Label>
                  <div className="border rounded-lg p-3 space-y-2">
                    {installerOptions.map((installer) => (
                      <div key={installer} className="flex items-center space-x-2">
                        <Checkbox
                          id={`protocol-installer-${installer}`}
                          checked={protocolInstallers.has(installer)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(protocolInstallers);
                            if (checked) newSet.add(installer);
                            else newSet.delete(installer);
                            setProtocolInstallers(newSet);
                          }}
                        />
                        <label htmlFor={`protocol-installer-${installer}`} className="text-sm cursor-pointer flex-1">
                          {installer}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="protocol-date">{t("contracts.installerDate")}</Label>
                    <DatePickerPopover
                      id="protocol-date"
                      value={protocolDate ? format(protocolDate, "yyyy-MM-dd") : ""}
                      onChange={(v) => {
                        if (v) setProtocolDate(new Date(v + "T00:00:00"));
                      }}
                      placeholder={t("contracts.selectDate")}
                      locale={dateLocale}
                      clearable={false}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="protocol-location">{t("contracts.locationLabel")}</Label>
                    <Input
                      id="protocol-location"
                      value={protocolLocation}
                      onChange={(e) => setProtocolLocation(e.target.value)}
                      placeholder={t("contracts.locationPlaceholder")}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setProtocolGeneratorDoc(null)} disabled={generateContractMutation.isPending}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  if (!protocolGeneratorDoc) return;
                  const parentQuote = documents.find(
                    (d) => d.document_type === "quote" &&
                      isDocumentRelatedToQuote(protocolGeneratorDoc, d.id, d.document_number ?? d.sf_document_id, d.sf_document_id)
                  );
                  const docIds = parentQuote
                    ? [parentQuote.id, protocolGeneratorDoc.id]
                    : [protocolGeneratorDoc.id];
                  const installerNamesStr = protocolInstallers.size > 0
                    ? Array.from(protocolInstallers).join(" a ")
                    : undefined;
                  const dateStr = protocolDate ? format(protocolDate, "dd.MM.yyyy", { locale: dateLocale }) : undefined;
                  generateContractMutation.mutate({
                    templateId: "odovzdavaci-protokol",
                    documentIds: docIds,
                    installerNames: installerNamesStr,
                    installerDate: dateStr,
                    location: protocolLocation || undefined,
                    contractDate: dateStr,
                  });
                }}
                disabled={generateContractMutation.isPending}
              >
                {generateContractMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("contracts.generating")}</>
                ) : (
                  <><ClipboardCheck className="h-4 w-4 mr-2" />{t("contracts.generateAndSave")}</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {emailFromContract && (
        <ContractEmailDialog
          dealId={dealId}
          dealEmail={dealEmail}
          contractId={emailFromContract.contractId}
          documentIds={emailFromContract.documentIds}
          pdfFilename={emailFromContract.pdfFilename}
          documents={documents}
          onClose={() => setEmailFromContract(null)}
          onSent={() => {
            setEmailFromContract(null);
            toast({ title: t("common.success"), description: t("documents.emailSent", "E-mail odoslaný.") });
          }}
        />
      )}

      {/* SF document email composer */}
      {sfDocEmail && sfDocEmailFiles && (
        <EmailComposer
          dealId={dealId}
          to={dealEmail ? [{ email: dealEmail }] : []}
          initialSubject={(() => {
            const doc = documents.find((d) => d.id === sfDocEmail.id);
            return doc?.document_number ?? doc?.document_name ?? "";
          })()}
          initialBody=""
          initialAttachmentFiles={sfDocEmailFiles}
          onClose={() => { setSfDocEmail(null); setSfDocEmailFiles(null); }}
          onSent={() => {
            setSfDocEmail(null);
            setSfDocEmailFiles(null);
            toast({ title: t("common.success"), description: t("documents.emailSent", "E-mail odoslaný.") });
          }}
        />
      )}
      {sfDocEmail && sfDocEmailLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-background rounded-lg p-4 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("common.loading")}
          </div>
        </div>
      )}
    </div>
  );
}

